import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { viewDataEntryService } from '../../services/viewDataEntryService';

import { trialService } from '../../services/trialService';
import { trialCentresService } from '../../services/trialCentresService';
import { locationManagementService } from '../../services/locationManagementService';
import { trialDataEntryApiService } from '../../services/trialDataEntryApiService';
import { 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  Calendar,
  MapPin,
  Sprout,
  Users,
  X,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,

  Download,
  FileText
} from 'lucide-react';

const ViewDataEntry = () => {
  const [showFilters, setShowFilters] = useState(true); // Always show filters initially
  const [filteredData, setFilteredData] = useState([]);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageForView, setSelectedImageForView] = useState(null);
  const [loadedImages, setLoadedImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTable, setShowTable] = useState(false); // Control table visibility


  // Helper function to parse API response arrays to comma-separated strings
  const parseObservationsArray = (observations) => {
    if (!observations || !Array.isArray(observations) || observations.length === 0) {
      return '-';
    }
    return observations.map(obs => `${obs.observation} (${obs.days} days)`).join(', ');
  };

  const parseCulturesArray = (trials) => {
    if (!trials || !Array.isArray(trials) || trials.length === 0) {
      return '-';
    }
    return trials.map(t => t.culture).join(', ');
  };

  // New state for cascading dropdowns
  const [selectedTrialId, setSelectedTrialId] = useState('');
  const [selectedCentreId, setSelectedCentreId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [uniqueTrials, setUniqueTrials] = useState([]);
  const [centresForTrial, setCentresForTrial] = useState([]);
  const [locationsForCentre, setLocationsForCentre] = useState([]);
  const [isLoadingCentres, setIsLoadingCentres] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isLoadingLocationDetails, setIsLoadingLocationDetails] = useState(false);
  
  // State for loaded data after Apply button
  const [loadedTrialData, setLoadedTrialData] = useState(null);
  const [loadedCentreData, setLoadedCentreData] = useState(null);
  const [loadedLocationData, setLoadedLocationData] = useState(null);
  
  // State for assignment data
  const [trialAssignments, setTrialAssignments] = useState([]);
  const [centreAssignments, setCentreAssignments] = useState([]);
  const [locationAssignments, setLocationAssignments] = useState([]);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  
  // State for pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm();


  const mockCentres = [
    { id: 1, name: 'Coimbatore Research Centre' },
    { id: 2, name: 'Salem Research Centre' },
    { id: 3, name: 'Erode Research Centre' },
    { id: 4, name: 'Madurai Research Centre' },
    { id: 5, name: 'Chennai Research Centre' },
  ];

  const mockLocations = [
    { id: 1, name: 'Coimbatore, Tamil Nadu' },
    { id: 2, name: 'Salem, Tamil Nadu' },
    { id: 3, name: 'Erode, Tamil Nadu' },
    { id: 4, name: 'Madurai, Tamil Nadu' },
    { id: 5, name: 'Chennai, Tamil Nadu' },
    { id: 6, name: 'Trichy, Tamil Nadu' },
  ];


  const [dataEntries, setDataEntries] = useState([]);

  // Load all trials from API
  const loadAllTrials = async () => {
    try {
      setIsLoading(true);
      const response = await trialService.getTrials({ page: 1, limit: 1000 });
      
      if (response.success) {
        const trials = response.data || response;
        const processedTrials = trials.map(trial => ({
          id: trial.id,
          name: trial.name || '-',
          code: trial.code || '-',
          crop: trial.crop_name || '-',
          season: trial.season_name || '-',
          year: trial.year || '-',
          status: trial.status || 'active'
        }));
        
        // Update unique trials
        const uniqueTrialNames = [...new Set(processedTrials.map(trial => trial.name))];
        const uniqueTrialsList = uniqueTrialNames.map(trialName => {
          const trial = processedTrials.find(t => t.name === trialName);
          return {
            id: trial.id,
            name: trial.name
          };
        });
        setUniqueTrials(uniqueTrialsList.sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        throw new Error('Failed to load trials');
      }
    } catch (error) {
      console.error('Failed to load trials from API:', error.message);
      setUniqueTrials([]);
      toast.error('Failed to load trials');
    } finally {
      setIsLoading(false);
    }
  };

  // Load centres for selected trial
  const loadCentresForTrial = async (trialId) => {
    try {
      setIsLoadingCentres(true);
      const response = await trialCentresService.getTrialCentres({
        page: 1,
        limit: 100,
        trial_id: trialId
      });
      
      if (response.success) {
        const centres = response.data.map(centre => ({
          id: centre.id,
          code: centre.code || centre.centre_code || '-',
          name: centre.name || centre.centre_name || '-',
          address: centre.address || '-',
          status: centre.status || 'active'
        }));
        setCentresForTrial(centres);
      } else {
        console.error('Failed to load centres:', response.message);
        setCentresForTrial([]);
      }
    } catch (error) {
      console.error('Error loading centres:', error);
      toast.error('Failed to load centres for selected trial');
      setCentresForTrial([]);
    } finally {
      setIsLoadingCentres(false);
    }
  };

  // Load locations for selected centre
  const loadLocationsForCentre = async (trialCentreId) => {
    try {
      setIsLoadingLocations(true);
      const response = await locationManagementService.getLocationManagement(trialCentreId, {
        page: 1,
        limit: 100
      });
      
      if (response.success) {
        const locations = response.data.map(location => ({
          id: location.id,
          locationName: location.location_name || '-',
          locationCode: location.sf_number || '-',
          culture: location.culture || location.culture_details || '-',
          treatment: location.treatment || '-',
          replications: location.replications || 1,
          observations: location.observations || [],
          observation: location.observation || location.observation_parameters || '-',
          lastEntryDate: location.last_entry_date || '-',
          startDate: location.from_date || '-',
          status: location.status || 'active',
          pendingEntries: location.pending_entries || 0,
          assignedOfficer: location.location_incharge || '-',
          address: location.address || '-',
          latitude: location.latitude || null,
          longitude: location.longitude || null
        }));
        setLocationsForCentre(locations);
      } else {
        console.error('Failed to load locations:', response.message);
        setLocationsForCentre([]);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      toast.error('Failed to load locations for selected centre');
      setLocationsForCentre([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Load trial data by ID
  const loadTrialById = async (trialId) => {
    try {
      const response = await trialService.getTrial(trialId);
      if (response.success) {
        const trial = response.data;
        console.log('Trial API Response:', trial); // Debug log
        
        // Parse observations array to comma-separated string
        const observationString = parseObservationsArray(trial.observations);
        
        // Parse trials array to comma-separated culture string
        const cultureString = parseCulturesArray(trial.trials);
        
        setLoadedTrialData({
          id: trial.id,
          name: trial.name || '-',
          code: trial.code || '-',
          crop: trial.crop_name || '-',
          season: trial.season_name || '-',
          year: trial.year || '-',
          status: trial.status || 'active',
          description: trial.description || '-',
          observation: observationString,
          culture: cultureString,
          trialType: trial.trial_type || 'MLT',
          replications: trial.replications || '-',
          trialIncharge: trial.trial_incharge || '-',
          // Store raw data for debugging
          rawObservations: trial.observations,
          rawCultures: trial.trials
        });
      }
    } catch (error) {
      console.error('Error loading trial data:', error);
      toast.error('Failed to load trial data');
    }
  };

  // Load centre data by ID
  const loadCentreById = async (centreId) => {
    try {
      const response = await trialCentresService.getTrialCentre(centreId);
      if (response.success) {
        const centre = response.data;
        setLoadedCentreData({
          id: centre.id,
          code: centre.code || centre.centre_code || '-',
          name: centre.name || centre.centre_name || '-',
          address: centre.address || '-',
          status: centre.status || 'active'
        });
      }
    } catch (error) {
      console.error('Error loading centre data:', error);
      toast.error('Failed to load centre data');
    }
  };

  // Load location data by ID
  const loadLocationById = async (locationId) => {
    try {
      const response = await locationManagementService.getLocationManagementById(locationId);
      if (response.success) {
        const location = response.data;
        console.log('Location API Response:', location); // Debug log
        setLoadedLocationData({
          id: location.id,
          locationName: location.location_name || '-',
          locationCode: location.sf_number || '-',
          culture: location.culture || location.culture_details || '-',
          treatment: location.treatment || '-',
          replications: location.replications || 1,
          observations: location.observations || [],
          observation: location.observation || location.observation_parameters || '-',
          lastEntryDate: location.last_entry_date || '-',
          startDate: location.from_date || '-',
          status: location.status || 'active',
          pendingEntries: location.pending_entries || 0,
          assignedOfficer: location.location_incharge || '-',
          address: location.address || '-',
          latitude: location.latitude || null,
          longitude: location.longitude || null
        });
      }
    } catch (error) {
      console.error('Error loading location data:', error);
      toast.error('Failed to load location data');
    }
  };

  // Load trial assignments
  const loadTrialAssignments = async (trialId) => {
    try {
      const response = await trialService.getTrialAssignments(trialId);
      if (response.success) {
        setTrialAssignments(response.data || []);
      } else {
        setTrialAssignments([]);
      }
    } catch (error) {
      console.error('Error loading trial assignments:', error);
      setTrialAssignments([]);
    }
  };

  // Load centre assignments
  const loadCentreAssignments = async (trialCentreId) => {
    try {
      const response = await trialCentresService.getTrialCentreAssignments(trialCentreId);
      if (response.success) {
        setCentreAssignments(response.data || []);
      } else {
        setCentreAssignments([]);
      }
    } catch (error) {
      console.error('Error loading centre assignments:', error);
      setCentreAssignments([]);
    }
  };

  // Load location assignments
  const loadLocationAssignments = async (locationId) => {
    try {
      const response = await locationManagementService.getLocationAssignments(locationId);
      if (response.success) {
        setLocationAssignments(response.data || []);
      } else {
        setLocationAssignments([]);
      }
    } catch (error) {
      console.error('Error loading location assignments:', error);
      setLocationAssignments([]);
    }
  };

  // Load data entries from API using location_id
  const loadDataEntries = async (locationId = null) => {
    if (!locationId) {
      console.warn('No location ID provided for loading data entries');
      setDataEntries([]);
      return [];
    }

    try {
      setIsLoading(true);

      
      const response = await trialDataEntryApiService.getTrialDataEntries({ location_id: locationId });
      if (response.success && response.data) {
        setDataEntries(response.data);

        // Update pagination if available
        if (response.pagination) {
          setPagination(response.pagination);
        }
        return response.data;
      } else {

        console.warn('API failed to load data entries');
        setDataEntries([]);
        return [];
      }
    } catch (error) {

      console.error('Failed to load data entries from API:', error.message);
      setDataEntries([]);
      toast.error('Failed to load data entries');
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {

    loadAllTrials();
  }, []);


  // Update centres when selected trial changes
  useEffect(() => {
    if (!selectedTrialId) {
      setCentresForTrial([]);
      setSelectedCentreId('');
      setSelectedLocationId('');
      setLocationsForCentre([]);
      return;
    }
    
    loadCentresForTrial(selectedTrialId);
  }, [selectedTrialId]);

  // Update locations when selected centre changes
  useEffect(() => {
    if (!selectedCentreId) {
      setLocationsForCentre([]);
      setSelectedLocationId('');
      return;
    }
    
    loadLocationsForCentre(selectedCentreId);
  }, [selectedCentreId]);

  // New handleApplyFilters function for cascading dropdowns
  const handleApplyFilters = async () => {
    if (!selectedTrialId || !selectedCentreId || !selectedLocationId) {
      toast.error('Please select Trial, Centre, and Location');
      return;
    }
    
    setIsLoadingLocationDetails(true);
    
    try {
      // Load fresh data from API using the selected IDs
      await Promise.all([
        loadTrialById(selectedTrialId),
        loadCentreById(selectedCentreId),
        loadLocationById(selectedLocationId)
      ]);
      
      // Load assignment data
      setIsLoadingAssignments(true);
      await Promise.all([
        loadTrialAssignments(selectedTrialId),
        loadCentreAssignments(selectedCentreId),
        loadLocationAssignments(selectedLocationId)
      ]);
      
      // Load data entries using location_id
      const loadedDataEntries = await loadDataEntries(selectedLocationId);
      
      // Create enhanced trial details from loaded data
      const trialData = loadedTrialData || {};
      const centreData = loadedCentreData || {};
      const locationData = loadedLocationData || {};
        
        setSelectedTrial({

        id: trialData.id,
        name: trialData.name || '-',
        trialCode: trialData.code || '-',
        trialType: trialData.trialType || 'MLT',
        year: trialData.year || '-',
        crop: trialData.crop || '-',
        season: trialData.season || '-',
        trialIncharge: trialData.trialIncharge || trialAssignments[0]?.officer || '-',
        status: trialData.status || 'Active',
        observation: trialData.observation || locationData.observation || 'Plant Height (21 days), Leaf Count (25 days)',
        replication: trialData.replications || locationData.replications || '5',
        culture: trialData.culture || locationData.culture || 'V1, V2',
        centre: centreData.name || '-',
        location: locationData.locationName || '-',
        address: locationData.address || '-',
        locationCode: locationData.locationCode || '-',
        startDate: locationData.startDate || '-',
        trialOfficer: trialAssignments[0]?.officer || '-',
        trialStatus: trialAssignments[0]?.status || 'Active',
        trialAssignmentDate: trialAssignments[0]?.assignmentDate || '-',
        centreOfficer: centreAssignments[0]?.officer || '-',
        centreStatus: centreAssignments[0]?.status || 'Active',
        centreAssignmentDate: centreAssignments[0]?.assignmentDate || '-',
        locationOfficer: locationAssignments[0]?.officer || '-',
        locationStatus: locationAssignments[0]?.status || 'Active',
        locationNotes: locationAssignments[0]?.notes || '-',
        locationAssignmentDate: locationAssignments[0]?.assignmentDate || '-'
      });
      
      // Set filtered data and show table using the returned data
      setFilteredData(loadedDataEntries);
      setShowTable(true);
      
      toast.success(`Found ${loadedDataEntries.length} data entries for selected location`);
    } catch (error) {
      console.error('Error loading location details:', error);
      toast.error('Failed to load location details');
    } finally {
      setIsLoadingLocationDetails(false);
      setIsLoadingAssignments(false);
    }
  };

  // Reset cascading dropdowns
  const handleResetFilters = () => {
    setSelectedTrialId('');
    setSelectedCentreId('');
    setSelectedLocationId('');
    setCentresForTrial([]);
    setLocationsForCentre([]);
    setSelectedTrial(null);

    setFilteredData([]);
    setDataEntries([]);
    setShowTable(false);
    setLoadedTrialData(null);
    setLoadedCentreData(null);
    setLoadedLocationData(null);
    setTrialAssignments([]);
    setCentreAssignments([]);
    setLocationAssignments([]);
    toast.success('Filters cleared');
  };

  // Handle view record
  const handleView = async (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
    
    // Load images for display
    if (record.image && record.image !== '[]') {
      try {
        setIsLoadingImages(true);
        const images = JSON.parse(record.image);
        const loadedImages = await loadImagesForDisplay(images);
        setLoadedImages(loadedImages);
      } catch (e) {
        console.warn('Error parsing image data:', e);
        setLoadedImages([]);
      } finally {
        setIsLoadingImages(false);
      }
    } else {
      setLoadedImages([]);
    }
  };

  // Handle edit record
  // Image handling functions
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => ({
      file,
      id: Date.now() + Math.random(),
      url: URL.createObjectURL(file),
      caption: file.name
    }));
    
    setSelectedImages(prev => [...prev, ...newImages]);
    setPreviewImages(prev => [...prev, ...newImages]);
  };

  const handleRemoveImage = (imageId) => {
    setSelectedImages(prev => prev.filter(img => img.id !== imageId));
    setPreviewImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleRemoveExistingImage = (index) => {
    setLoadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleViewImage = (image) => {
    setSelectedImageForView(image);
    setShowImageModal(true);
  };

  // Download and display image using downloadDocument API
  const downloadAndDisplayImage = async (imageId) => {
    try {
      const blob = await trialDataEntryApiService.downloadDocument(imageId);
      const imageUrl = URL.createObjectURL(blob);
      return imageUrl;
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to load image');
      return null;
    }
  };

  // Load images for display
  const loadImagesForDisplay = async (images) => {
    const loadedImages = [];
    
    for (const image of images) {
      if (image.id) {
        // If image has an ID, download it using the API
        const imageUrl = await downloadAndDisplayImage(image.id);
        if (imageUrl) {
          loadedImages.push({
            ...image,
            url: imageUrl,
            loaded: true
          });
        }
      } else if (image.url) {
        // If image already has a URL, use it directly
        loadedImages.push({
          ...image,
          loaded: true
        });
      }
    }
    
    return loadedImages;
  };

  // Cleanup function to revoke object URLs
  const cleanupImageUrls = () => {
    loadedImages.forEach(image => {
      if (image.url && image.url.startsWith('blob:')) {
        URL.revokeObjectURL(image.url);
      }
    });
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupImageUrls();
    };
  }, [loadedImages]);

  const handleEdit = async (entry) => {
    setSelectedRecord(entry);
    
    // Initialize with existing images from the image field
    if (entry.image && entry.image !== '[]') {
      try {
        const images = JSON.parse(entry.image);
        const loadedImages = await loadImagesForDisplay(images);
        
        const existingImages = loadedImages.map((img, index) => ({
          id: img.id || `existing-${index}`,
          url: img.url,
          caption: img.caption,
          isExisting: true
        }));
        
        setSelectedImages(existingImages);
        setPreviewImages(existingImages);
        setLoadedImages(loadedImages);
      } catch (e) {
        console.warn('Error parsing image data in edit:', e);
        setSelectedImages([]);
        setPreviewImages([]);
        setLoadedImages([]);
      }
    } else {
      setSelectedImages([]);
      setPreviewImages([]);
      setLoadedImages([]);
    }
    setShowEditModal(true);
  };

  // Handle delete record
  const handleDelete = (record) => {
    setSelectedRecord(record);
    setShowDeleteModal(true);
  };

  // Confirm delete

  const confirmDelete = async () => {
    if (!selectedRecord) return;
    
    try {
      setIsLoading(true);
      const response = await trialDataEntryApiService.deleteTrialDataEntry(selectedRecord.id);
      
      if (response.success) {
        // Remove from local state
      setDataEntries(prev => prev.filter(entry => entry.id !== selectedRecord.id));
      setFilteredData(prev => prev.filter(entry => entry.id !== selectedRecord.id));
      setShowDeleteModal(false);
      setSelectedRecord(null);

        toast.success('Data entry deleted successfully');
      } else {
        toast.error('Failed to delete data entry');
      }
    } catch (error) {
      console.error('Error deleting data entry:', error);
      toast.error(error.message || 'Failed to delete data entry');
    } finally {
      setIsLoading(false);
    }
  };

  // Save edited data entry
  const handleSaveEdit = async () => {
    if (!selectedRecord) return;
    
    try {
      setIsLoading(true);
      
      // Handle image uploads for new images
      const uploadedImageData = [];
      
      // Upload new images
      for (const image of selectedImages) {
        if (image.file && !image.isExisting) {
          try {
            const uploadResponse = await trialDataEntryApiService.uploadImage(image.file);
            if (uploadResponse.success) {
              uploadedImageData.push({
                id: uploadResponse.data.id,
                url: uploadResponse.data.url,
                caption: image.caption || image.file.name
              });
            }
          } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(`Failed to upload image: ${image.file.name}`);
          }
        }
      }
      
      // Combine existing images (that weren't removed) with new uploaded images
      const finalImages = [
        ...loadedImages.map(img => ({
          id: img.id,
          url: img.url,
          caption: img.caption
        })),
        ...uploadedImageData
      ];
      
      // Prepare the data for API update
      const updateData = {
        entry_date: selectedRecord.entry_date,
        culture: selectedRecord.culture,
        observation: selectedRecord.observation,
        replication: selectedRecord.replication,
        value: selectedRecord.value,
        status: selectedRecord.status,
        farmer_feedback: selectedRecord.farmer_feedback,
        incharge_feedback: selectedRecord.incharge_feedback,
        image: JSON.stringify(finalImages)
      };
      
      const response = await trialDataEntryApiService.updateTrialDataEntry(selectedRecord.id, updateData);
      
      if (response.success) {
        // Update local state with the updated data
        setDataEntries(prev => prev.map(entry => 
          entry.id === selectedRecord.id ? { ...entry, ...updateData } : entry
        ));
        setFilteredData(prev => prev.map(entry => 
          entry.id === selectedRecord.id ? { ...entry, ...updateData } : entry
        ));
        
        setShowEditModal(false);
        setSelectedImages([]);
        setPreviewImages([]);
        cleanupImageUrls();
        setLoadedImages([]);
        toast.success('Data entry updated successfully');
      } else {
        toast.error('Failed to update data entry');
      }
    } catch (error) {
      console.error('Error updating data entry:', error);
      toast.error(error.message || 'Failed to update data entry');
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Completed': return 'bg-blue-100 text-blue-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">View Data Entry</h1>
          <p className="text-gray-600 mt-1">Select trial details to view and manage data entries</p>
        </div>
      </div>


      {/* Filter Section - Enhanced with Cascading Dropdowns */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">

        <div className="flex items-center space-x-2 mb-4">
          <FileText className="w-6 h-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Select Trial Details</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Trial Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trial Name <span className="text-red-500">*</span>
              </label>
              <select

              value={selectedTrialId}
              onChange={(e) => {
                setSelectedTrialId(e.target.value);
                setSelectedCentreId('');
                setSelectedLocationId('');
                setShowTable(false);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Trial</option>

              {uniqueTrials.map((trial) => (
                <option key={trial.id} value={trial.id}>
                    {trial.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Centre Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Centre Name <span className="text-red-500">*</span>
              </label>
              <select

              value={selectedCentreId}
              onChange={(e) => {
                setSelectedCentreId(e.target.value);
                setSelectedLocationId('');
                setShowTable(false);
              }}
              disabled={!selectedTrialId || isLoadingCentres}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">
                {isLoadingCentres ? 'Loading centres...' : 'Select Centre'}
              </option>
              {centresForTrial.map((centre) => (
                <option key={centre.id} value={centre.id}>
                    {centre.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Name Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location Name <span className="text-red-500">*</span>
              </label>
              <select

              value={selectedLocationId}
              onChange={(e) => {
                setSelectedLocationId(e.target.value);
                setShowTable(false);
              }}
              disabled={!selectedCentreId || isLoadingLocations}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">
                {isLoadingLocations ? 'Loading locations...' : 'Select Location'}
              </option>
              {locationsForCentre.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.locationName}
                  </option>
                ))}
              </select>
            </div>
          </div>


        {/* Action Buttons */}
        <div className="flex justify-center gap-4">
            <button

            onClick={handleApplyFilters}
            disabled={!selectedTrialId || !selectedCentreId || !selectedLocationId || isLoadingLocationDetails}
            className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingLocationDetails ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>

                <span>Loading...</span>
                </div>
              ) : (
                'Apply'
              )}
            </button>
            <button

            onClick={handleResetFilters}
            className="bg-white hover:bg-gray-50 text-gray-700 font-medium py-3 px-6 rounded-lg border border-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Reset
            </button>
          </div>
      </div>

      {/* Selected Trial Details */}
      {selectedTrial && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-4">
            <MapPin className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Location Details</h3>

            {isLoadingLocationDetails && (
              <div className="ml-auto">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  <span>Loading details...</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Trial Header Info */}
          <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-100 rounded flex items-center justify-center">
                <span className="text-xs">🏠</span>
              </div>
              <span className="text-gray-600">Trial:</span>

              <span className="font-medium">{loadedTrialData?.code || selectedTrial.trialCode || 'MLTT25K002'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-100 rounded flex items-center justify-center">
                <span className="text-xs">🏢</span>
              </div>
              <span className="text-gray-600">Centre:</span>

              <span className="font-medium">{loadedCentreData?.name || selectedTrial.centre || '-'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-100 rounded flex items-center justify-center">
                <MapPin className="w-3 h-3" />
              </div>
              <span className="text-gray-600">Location:</span>

              <span className="font-medium">{loadedLocationData?.locationName || selectedTrial.location || '-'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-100 rounded flex items-center justify-center">
                <span className="text-xs">🏷️</span>
              </div>
              <span className="text-gray-600">Type:</span>

              <span className="font-medium">{loadedTrialData?.trialType || selectedTrial.trialType || 'MLT'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-100 rounded flex items-center justify-center">
                <Calendar className="w-3 h-3" />
              </div>
              <span className="text-gray-600">Year:</span>

              <span className="font-medium">{loadedTrialData?.year || selectedTrial.year || '2025'}</span>
            </div>

            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-100 rounded flex items-center justify-center">
                <Sprout className="w-3 h-3" />
          </div>

              <span className="text-gray-600">Crop:</span>
              <span className="font-medium">{loadedTrialData?.crop || selectedTrial.crop || '-'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-100 rounded flex items-center justify-center">
                <Calendar className="w-3 h-3" />
              </div>
              <span className="text-gray-600">Season:</span>
              <span className="font-medium">{loadedTrialData?.season || selectedTrial.season || '-'}</span>
            </div>
          </div>



          {/* Location Information */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Location Information</h4>
            <div className="space-y-3">

              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Location Code:</p>
                <p className="text-sm text-gray-900">{loadedLocationData?.locationCode || selectedTrial.locationCode || '-'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Location Name:</p>
                <p className="text-sm text-gray-900">{loadedLocationData?.locationName || selectedTrial.location || '-'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Address:</p>
                <p className="text-sm text-gray-900">{loadedLocationData?.address || selectedTrial.address || '-'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Assigned Officer:</p>
                <p className="text-sm text-gray-900">{loadedLocationData?.assignedOfficer || selectedTrial.locationOfficer || '-'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Start Date:</p>
                <p className="text-sm text-gray-900">{loadedLocationData?.startDate || selectedTrial.startDate || '-'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Observation:</p>

                <p className="text-sm text-gray-900">{loadedTrialData?.observation || loadedLocationData?.observation || selectedTrial.observation || 'Plant Height (21 days), Leaf Count (25 days)'}</p>
              </div>
              <div className="flex items-start">

                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Replications:</p>
                <p className="text-sm text-gray-900">{loadedLocationData?.replications || selectedTrial.replication || '5'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Culture:</p>

                <p className="text-sm text-gray-900">{loadedTrialData?.culture || loadedLocationData?.culture || selectedTrial.culture || 'V1, V2'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Treatment:</p>
                <p className="text-sm text-gray-900">{loadedLocationData?.treatment || selectedTrial.treatment || '-'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Trial Type:</p>

                <p className="text-sm text-gray-900">{loadedTrialData?.trialType || selectedTrial.trialType || 'MLT'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Year:</p>

                <p className="text-sm text-gray-900">{loadedTrialData?.year || selectedTrial.year || '2025'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Crop:</p>

                <p className="text-sm text-gray-900">{loadedTrialData?.crop || selectedTrial.crop || 'Paddy'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Season:</p>

                <p className="text-sm text-gray-900">{loadedTrialData?.season || selectedTrial.season || 'Kharif'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Trial Incharge:</p>

                <p className="text-sm text-gray-900">{loadedTrialData?.trialIncharge || selectedTrial.trialIncharge || 'Dr. Rajesh Kumar'}</p>
              </div>
              <div className="flex items-start">
                <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Status:</p>

                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  (loadedLocationData?.status || selectedTrial.status || 'Active').toLowerCase() === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : (loadedLocationData?.status || selectedTrial.status || 'Active').toLowerCase() === 'completed'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {loadedLocationData?.status || selectedTrial.status || 'Active'}
                </span>
              </div>

              {(loadedLocationData?.latitude && loadedLocationData?.longitude) && (
                <div className="flex items-start">
                  <p className="text-sm font-medium text-gray-700 w-32 flex-shrink-0">Coordinates:</p>
                  <p className="text-sm text-gray-900">
                    {loadedLocationData.latitude}, {loadedLocationData.longitude}
                  </p>
                </div>
              )}
            </div>
          </div>


          {/* Additional Location Details */}
          {loadedLocationData && (
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Additional Location Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3">Location Information</h5>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Code:</p>
                      <p className="text-sm text-gray-900">{loadedLocationData.locationCode || '-'}</p>
                    </div>
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Name:</p>
                      <p className="text-sm text-gray-900">{loadedLocationData.locationName || '-'}</p>
                    </div>
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Address:</p>
                      <p className="text-sm text-gray-900">{loadedLocationData.address || '-'}</p>
                    </div>
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Officer:</p>
                      <p className="text-sm text-gray-900">{loadedLocationData.assignedOfficer || '-'}</p>
                    </div>
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Start Date:</p>
                      <p className="text-sm text-gray-900">{loadedLocationData.startDate || '-'}</p>
                    </div>
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Status:</p>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        loadedLocationData.status?.toLowerCase() === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : loadedLocationData.status?.toLowerCase() === 'completed'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {loadedLocationData.status || 'Active'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3">Trial Configuration</h5>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Observation:</p>
                      <p className="text-sm text-gray-900">{loadedTrialData?.observation || loadedLocationData?.observation || 'Plant Height (21 days), Leaf Count (25 days)'}</p>
                    </div>
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Replications:</p>
                      <p className="text-sm text-gray-900">{loadedLocationData.replications || '5'}</p>
                    </div>
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Culture:</p>
                      <p className="text-sm text-gray-900">{loadedTrialData?.culture || loadedLocationData?.culture || 'V1, V2'}</p>
                    </div>
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Treatment:</p>
                      <p className="text-sm text-gray-900">{loadedLocationData.treatment || '-'}</p>
                    </div>
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Last Entry:</p>
                      <p className="text-sm text-gray-900">{loadedLocationData.lastEntryDate || '-'}</p>
                    </div>
                    <div className="flex items-start">
                      <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Pending:</p>
                      <p className="text-sm text-gray-900">{loadedLocationData.pendingEntries || '0'} entries</p>
                    </div>
                    {(loadedLocationData.latitude && loadedLocationData.longitude) && (
                      <div className="flex items-start">
                        <p className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">Coordinates:</p>
                        <p className="text-sm text-gray-900">
                          {loadedLocationData.latitude}, {loadedLocationData.longitude}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Assignment Details */}
          <div>

            <div className="flex items-center space-x-2 mb-4">
              <h4 className="text-md font-semibold text-gray-900">Assignment Details</h4>
              {isLoadingAssignments && (
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                  <span>Loading assignments...</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Trial Assignments */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="text-sm font-semibold text-gray-900 mb-3">Trial Assignments</h5>

                {trialAssignments && trialAssignments.length > 0 ? (
                  <div className="space-y-4">
                    {trialAssignments.map((assignment, index) => (
                      <div key={assignment.id || index} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="space-y-2">
                  <div>
                    <p className="text-xs font-medium text-gray-700">Officer:</p>

                            <p className="text-sm text-gray-900">
                              {assignment.officer_name || selectedTrial.trialOfficer || 'Dr. Rajesh Kumar'}
                            </p>
                  </div>

                          {assignment.officer_email && (
                            <div>
                              <p className="text-xs font-medium text-gray-700">Email:</p>
                              <p className="text-sm text-gray-600">{assignment.officer_email}</p>
                            </div>
                          )}
                  <div>
                    <p className="text-xs font-medium text-gray-700">Status:</p>

                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (assignment.status || selectedTrial.trialStatus || 'Active').toLowerCase() === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : (assignment.status || selectedTrial.trialStatus || 'Active').toLowerCase() === 'completed'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {assignment.status || selectedTrial.trialStatus || 'Active'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-700">Assignment Date:</p>

                            <p className="text-sm text-gray-900">
                              {assignment.assignment_date ? 
                                new Date(assignment.assignment_date).toLocaleDateString('en-IN') : 
                                selectedTrial.trialAssignmentDate || '2025-09-10T00:00:00.000Z'
                              }
                            </p>
                  </div>

                          {assignment.centre_incharge && (
                            <div>
                              <p className="text-xs font-medium text-gray-700">Centre Incharge:</p>
                              <p className="text-sm text-gray-900">{assignment.centre_incharge}</p>
                </div>

                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">No trial assignments found</p>
                  </div>
                )}
              </div>

              {/* Centre Assignments */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="text-sm font-semibold text-gray-900 mb-3">Centre Assignments</h5>

                {centreAssignments && centreAssignments.length > 0 ? (
                  <div className="space-y-4">
                    {centreAssignments.map((assignment, index) => (
                      <div key={assignment.id || index} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="space-y-2">
                  <div>
                    <p className="text-xs font-medium text-gray-700">Officer:</p>

                            <p className="text-sm text-gray-900">
                              {assignment.officer_name || assignment.officer || selectedTrial.centreOfficer || 'Dr. Priya Sharma'}
                            </p>
                  </div>

                          {assignment.officer_email && (
                            <div>
                              <p className="text-xs font-medium text-gray-700">Email:</p>
                              <p className="text-sm text-gray-600">{assignment.officer_email}</p>
                            </div>
                          )}
                  <div>
                    <p className="text-xs font-medium text-gray-700">Status:</p>

                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (assignment.status || selectedTrial.centreStatus || 'Active').toLowerCase() === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : (assignment.status || selectedTrial.centreStatus || 'Active').toLowerCase() === 'completed'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {assignment.status || selectedTrial.centreStatus || 'Active'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-700">Assignment Date:</p>

                            <p className="text-sm text-gray-900">
                              {assignment.assignment_date ? 
                                new Date(assignment.assignment_date).toLocaleDateString('en-IN') : 
                                assignment.assignmentDate ? 
                                new Date(assignment.assignmentDate).toLocaleDateString('en-IN') :
                                selectedTrial.centreAssignmentDate || '2025-09-13T00:00:00.000Z'
                              }
                            </p>
                  </div>

                          {assignment.notes && (
                            <div>
                              <p className="text-xs font-medium text-gray-700">Notes:</p>
                              <p className="text-sm text-gray-900">{assignment.notes}</p>
                </div>

                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">No centre assignments found</p>
                  </div>
                )}
              </div>

              {/* Location Assignments */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="text-sm font-semibold text-gray-900 mb-3">Location Assignments</h5>

                {locationAssignments && locationAssignments.length > 0 ? (
                  <div className="space-y-4">
                    {locationAssignments.map((assignment, index) => (
                      <div key={assignment.id || index} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="space-y-2">
                  <div>
                    <p className="text-xs font-medium text-gray-700">Officer:</p>

                            <p className="text-sm text-gray-900">
                              {assignment.officer_name || assignment.officer || selectedTrial.locationOfficer || 'Dr. Rajesh Kumar'}
                            </p>
                  </div>

                          {assignment.officer_email && (
                            <div>
                              <p className="text-xs font-medium text-gray-700">Email:</p>
                              <p className="text-sm text-gray-600">{assignment.officer_email}</p>
                            </div>
                          )}
                  <div>
                    <p className="text-xs font-medium text-gray-700">Status:</p>

                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              (assignment.status || selectedTrial.locationStatus || 'Active').toLowerCase() === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : (assignment.status || selectedTrial.locationStatus || 'Active').toLowerCase() === 'completed'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {assignment.status || selectedTrial.locationStatus || 'Active'}
                    </span>
                  </div>
                  <div>

                            <p className="text-xs font-medium text-gray-700">Assignment Date:</p>
                            <p className="text-sm text-gray-900">
                              {assignment.assignment_date ? 
                                new Date(assignment.assignment_date).toLocaleDateString('en-IN') : 
                                assignment.assignmentDate ? 
                                new Date(assignment.assignmentDate).toLocaleDateString('en-IN') :
                                selectedTrial.locationAssignmentDate || '2025-09-20T00:00:00.000Z'
                              }
                            </p>
                  </div>
                  <div>

                            <p className="text-xs font-medium text-gray-700">Notes:</p>
                            <p className="text-sm text-gray-900">
                              {assignment.notes || selectedTrial.locationNotes || 'Primary location incharge'}
                            </p>
                  </div>
                </div>
              </div>

                    ))}
            </div>

                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500">No location assignments found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table - Only show after applying filters */}
      {showTable && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Data Entries {filteredData.length > 0 && `(${filteredData.length})`}
            </h3>
          </div>
        
        {filteredData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">

                    Culture
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Observation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Replication
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">

                    Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Officer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">

                      {entry.entry_date ? new Date(entry.entry_date).toLocaleDateString('en-IN') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">

                      {entry.culture || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">

                      {entry.observation || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">

                      {entry.replication || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">

                      {entry.value !== null && entry.value !== undefined ? entry.value : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        entry.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : entry.status === 'completed'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {entry.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{entry.officer_name || '-'}</div>
                        {entry.officer_email && (
                          <div className="text-xs text-gray-500">{entry.officer_email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(entry)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(entry)}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Edit Record"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(entry)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Delete Record"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No data entries found</h3>
            <p className="text-gray-500">No data entries match the selected filters. Try adjusting your search criteria.</p>
          </div>
        )}

        
        {/* Pagination Information */}
        {filteredData.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{filteredData.length}</span> of{' '}
                <span className="font-medium">{pagination.totalItems}</span> entries
              </div>
              <div className="text-sm text-gray-500">
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
            </div>
          </div>
        )}
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedRecord && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowViewModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Data Entry Details</h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* Data Entry Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Data Entry Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date</label>

                        <p className="mt-1 text-sm text-gray-900">
                          {selectedRecord.entry_date ? new Date(selectedRecord.entry_date).toLocaleDateString('en-IN') : '-'}
                        </p>
                      </div>
                      <div>

                        <label className="block text-sm font-medium text-gray-700">Culture</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedRecord.culture || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Observation</label>

                        <p className="mt-1 text-sm text-gray-900">{selectedRecord.observation || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Replication</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono">{selectedRecord.replication || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Value</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedRecord.value !== null && selectedRecord.value !== undefined ? selectedRecord.value : '-'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          selectedRecord.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : selectedRecord.status === 'completed'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedRecord.status || 'Unknown'}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Officer</label>

                        <div>
                          <p className="text-sm text-gray-900">{selectedRecord.officer_name || '-'}</p>
                          {selectedRecord.officer_email && (
                            <p className="text-xs text-gray-500">{selectedRecord.officer_email}</p>
                          )}
                        </div>
                      </div>
                      <div>

                        <label className="block text-sm font-medium text-gray-700">Location</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedRecord.location_name || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Farmer Feedback */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Farmer Feedback</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Feedback</label>
                        <p className="mt-1 text-sm text-gray-900 bg-white p-3 rounded border">

                          {selectedRecord.farmer_feedback || 'No farmer feedback available for this entry.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Location Incharge Feedback */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Location Incharge Feedback</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Feedback</label>
                        <p className="mt-1 text-sm text-gray-900 bg-white p-3 rounded border">

                          {selectedRecord.incharge_feedback || 'No incharge feedback available for this entry.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Images */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
                    
                    {isLoadingImages ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        <p className="text-gray-500">Loading images...</p>
                      </div>
                    ) : loadedImages.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {loadedImages.map((image, index) => (
                          <div key={index} className="border rounded-lg p-4">
                            <div 
                              className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg mb-2 cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => handleViewImage(image)}
                            >
                              <img
                                src={image.url}
                                alt={image.caption || `Image ${index + 1}`}
                                className="w-full h-48 object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                                }}
                              />
                            </div>
                            {image.caption && (
                              <p className="text-sm text-gray-600 mt-2 truncate">{image.caption}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">No images uploaded for this entry</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    cleanupImageUrls();
                    setLoadedImages([]);
                  }}
                  className="btn-primary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedRecord && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
              setShowEditModal(false);
              setSelectedImages([]);
              setPreviewImages([]);
              cleanupImageUrls();
              setLoadedImages([]);
            }}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Edit Data Entry</h3>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedImages([]);
                      setPreviewImages([]);
                      cleanupImageUrls();
                      setLoadedImages([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Trial Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Trial Information</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Trial Name</label>

                        <p className="mt-1 text-sm text-gray-900">{selectedRecord.trialName || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Culture Name</label>

                        <p className="mt-1 text-sm text-gray-900">{selectedRecord.culture || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Observation</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedRecord.observation}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Date</label>

                        <p className="mt-1 text-sm text-gray-900">{selectedRecord.entry_date ? formatDate(selectedRecord.entry_date) : 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Replication Data Table */}
                  <div className="bg-white rounded-lg border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h4 className="text-md font-semibold text-gray-900">Replication Data</h4>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Culture
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Observation
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Replication
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Value
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">

                          {/* Display single replication and value row for API response format */}
                          {(() => {

                            // Handle API response format where replication is a simple string and value is a number
                            const replication = selectedRecord.replication || '';
                            const value = selectedRecord.value !== null && selectedRecord.value !== undefined ? selectedRecord.value : '';
                              
                              return (

                              <tr className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <input
                                      type="date"

                                    value={selectedRecord.entry_date ? selectedRecord.entry_date.split('T')[0] : ''}
                                    onChange={(e) => setSelectedRecord(prev => ({ ...prev, entry_date: e.target.value + 'T00:00:00.000Z' }))}
                                      className="input-field text-sm"
                                    />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <select

                                    value={selectedRecord.culture || ''}
                                    onChange={(e) => setSelectedRecord(prev => ({ ...prev, culture: e.target.value }))}
                                      className="input-field text-sm"
                                    >

                                    <option value="v5">v5</option>
                                    <option value="v6">v6</option>
                                    <option value="v7">v7</option>
                                    </select>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <select

                                    value={selectedRecord.observation || ''}
                                    onChange={(e) => setSelectedRecord(prev => ({ ...prev, observation: e.target.value }))}
                                      className="input-field text-sm"
                                    >

                                    <option value="observation 1 (20 days)">observation 1 (20 days)</option>
                                    <option value="observation 2 (25 days)">observation 2 (25 days)</option>
                                    <option value="observation 3 (30 days)">observation 3 (30 days)</option>
                                    </select>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <input
                                      type="text"

                                    value={replication}
                                    onChange={(e) => setSelectedRecord(prev => ({ ...prev, replication: e.target.value }))}
                                      className="input-field text-sm"

                                    placeholder="Replication"
                                    />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <input

                                    type="number"
                                    value={value}
                                    onChange={(e) => setSelectedRecord(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                                      className="input-field text-sm"

                                    placeholder="Value"
                                    />
                                  </td>

                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  <select
                                    value={selectedRecord.status || ''}
                                    onChange={(e) => setSelectedRecord(prev => ({ ...prev, status: e.target.value }))}
                                    className="input-field text-sm"
                                  >
                                    <option value="active">Active</option>
                                    <option value="completed">Completed</option>
                                    <option value="pending">Pending</option>
                                  </select>
                                  </td>
                                </tr>
                              );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Farmer Feedback */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Farmer Feedback</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Feedback</label>
                      <textarea

                        value={selectedRecord.farmer_feedback || ''}
                        onChange={(e) => setSelectedRecord(prev => ({ ...prev, farmer_feedback: e.target.value }))}
                        className="input-field mt-1 h-24 resize-none"
                        placeholder="Enter farmer feedback..."
                      />
                    </div>
                  </div>

                  {/* Location Incharge Feedback */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Location Incharge Feedback</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Feedback</label>
                      <textarea

                        value={selectedRecord.incharge_feedback || ''}
                        onChange={(e) => setSelectedRecord(prev => ({ ...prev, incharge_feedback: e.target.value }))}
                        className="input-field mt-1 h-24 resize-none"
                        placeholder="Enter location incharge feedback..."
                      />
                    </div>
                  </div>

                  {/* Field Photos & Documentation */}
                  <div className="bg-white rounded-lg border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h4 className="text-md font-semibold text-gray-900">Field Photos & Documentation</h4>
                    </div>
                    <div className="p-6">
                      {/* Upload Area */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">Click to upload photos or drag and drop</p>
                          <p className="text-xs text-gray-500">Supports JPG, PNG, GIF (Max 5MB each)</p>
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            className="hidden"
                            id="image-upload"
                            onChange={handleImageUpload}
                          />
                          <label
                            htmlFor="image-upload"
                            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer"
                          >
                            Choose Files
                          </label>
                        </div>
                      </div>

                      {/* Newly Selected Images */}
                      {previewImages && previewImages.filter(img => !img.isExisting).length > 0 && (
                        <div className="mt-6">
                          <h5 className="text-sm font-medium text-gray-700 mb-3">
                            New Images 
                            <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              {previewImages.filter(img => !img.isExisting).length} added
                            </span>
                          </h5>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {previewImages.filter(img => !img.isExisting).map((image, index) => (
                              <div key={image.id} className="relative group">
                                <div className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg overflow-hidden">
                                  <img
                                    src={image.url}
                                    alt={image.caption || `Image ${index + 1}`}
                                    className="w-full h-24 object-cover"
                                    onError={(e) => {
                                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                                    }}
                                  />
                                </div>
                                <button
                                  onClick={() => handleRemoveImage(image.id)}
                                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                                  title="Remove Image"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Existing Images */}
                      {loadedImages && loadedImages.length > 0 && (
                        <div className="mt-6">
                          <h5 className="text-sm font-medium text-gray-700 mb-3">
                            Existing Images
                            <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                              {loadedImages.length} remaining
                            </span>
                          </h5>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {loadedImages.map((image, index) => (
                              <div key={`existing-${index}`} className="relative group">
                                <div 
                                  className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => handleViewImage(image)}
                                >
                                  <img
                                    src={image.url}
                                    alt={image.caption || `Image ${index + 1}`}
                                    className="w-full h-24 object-cover"
                                    onError={(e) => {
                                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                                    }}
                                  />
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveExistingImage(index);
                                  }}
                                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                                  title="Remove Image"
                                >
                                  ×
                                </button>
                                <div className="mt-2">
                                  <p className="text-xs text-gray-600 truncate">
                                    {image.caption || `Image ${index + 1}`}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No Images Message */}
                      {(!previewImages || previewImages.length === 0) && (!loadedImages || loadedImages.length === 0) && (
                        <div className="mt-6 text-center py-8 bg-gray-50 rounded-lg">
                          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-2">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <p className="text-gray-500 text-sm">No images available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"

                  onClick={handleSaveEdit}
                  disabled={isLoading}
                  className="btn-primary ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Saving...</span>
                    </div>
                  ) : (
                    'Save All Changes'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedImages([]);
                    setPreviewImages([]);
                    cleanupImageUrls();
                    setLoadedImages([]);
                  }}
                  className="btn-outline"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedRecord && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Data Entry
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this data entry? This action cannot be undone.
                      </p>
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700">

                          <strong>Date:</strong> {selectedRecord.entry_date ? formatDate(selectedRecord.entry_date) : 'N/A'}<br/>
                          <strong>Culture:</strong> {selectedRecord.culture || 'N/A'}<br/>
                          <strong>Observation:</strong> {selectedRecord.observation || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDelete}

                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Deleting...</span>
                    </div>
                  ) : (
                    'Delete'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image View Modal */}
      {showImageModal && selectedImageForView && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" onClick={() => setShowImageModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Image View</h3>
                  <button
                    onClick={() => setShowImageModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="text-center">
                  <div className="max-w-4xl mx-auto">
                    <img
                      src={selectedImageForView.url}
                      alt={selectedImageForView.caption || 'Image'}
                      className="max-w-full max-h-96 mx-auto rounded-lg shadow-lg"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                      }}
                    />
                    <p className="mt-4 text-sm text-gray-600">
                      {selectedImageForView.caption || 'Image'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setShowImageModal(false)}
                  className="btn-primary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDataEntry;

